/**
 * Network Execution — Enforcement Mode Resolver (Phase 1c)
 * ─────────────────────────────────────────────────────────
 * Single source of truth for soft vs hard behavior across the entire
 * 1c layer. Per RECONCILIATION.md and the locks recorded for 1c:
 *
 *   • Mode is BEHAVIOR-DRIVEN ONLY. Lifecycle states, writers, queues,
 *     and telemetry are IDENTICAL in both modes.
 *   • Mode gates exactly three switches inside writers:
 *       - requiresManualConfirmation
 *       - autoExecutesSideEffects
 *       - escalationChannel ("advisory" | "actioned")
 *   • Default during alpha: "soft".
 *   • Flipping to "hard" is super_admin-only, audited, and emits a
 *     visible system banner (UI consumes `getEnforcementModeBanner()`).
 *
 * No duplicated soft/hard code paths. Any consumer that branches on
 * mode for anything other than the three switches above is a bug.
 *
 * Anchor: mem://architecture/network-execution-phase-1c (added on commit)
 */

export type NetworkEnforcementMode = "soft" | "hard";

const STORAGE_KEY = "network_enforcement_mode";
const AUDIT_STORAGE_KEY = "network_enforcement_mode_audit";
const BANNER_TTL_MS = 24 * 60 * 60 * 1000; // 24h visible banner after flip

const DEFAULT_MODE: NetworkEnforcementMode = "soft";

export interface EnforcementModeBehavior {
  /** Writer must require an explicit operator confirm step. */
  requiresManualConfirmation: boolean;
  /** Writer is allowed to perform side-effects (state writes) without a human gesture. */
  autoExecutesSideEffects: boolean;
  /** "advisory" = log + surface. "actioned" = perform + notify. */
  escalationChannel: "advisory" | "actioned";
}

const BEHAVIOR_TABLE: Record<NetworkEnforcementMode, EnforcementModeBehavior> = {
  soft: {
    requiresManualConfirmation: true,
    autoExecutesSideEffects: false,
    escalationChannel: "advisory",
  },
  hard: {
    requiresManualConfirmation: false,
    autoExecutesSideEffects: true,
    escalationChannel: "actioned",
  },
};

export interface EnforcementModeAuditEntry {
  at: string;
  fromMode: NetworkEnforcementMode;
  toMode: NetworkEnforcementMode;
  actorId: string;
  actorRole: string;
  reason: string;
}

function safeStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** Single read path. All 1c writers must call this resolver. */
export function getNetworkEnforcementMode(): NetworkEnforcementMode {
  const s = safeStorage();
  if (!s) return DEFAULT_MODE;
  const raw = s.getItem(STORAGE_KEY);
  return raw === "hard" ? "hard" : DEFAULT_MODE;
}

/** Behavior switches derived from current mode. */
export function getEnforcementBehavior(
  mode: NetworkEnforcementMode = getNetworkEnforcementMode(),
): EnforcementModeBehavior {
  return BEHAVIOR_TABLE[mode];
}

export interface SetEnforcementModeInput {
  toMode: NetworkEnforcementMode;
  actorId: string;
  actorRole: string;
  reason: string;
  /** Caller MUST gate on role === "super_admin" before calling. */
  confirmedSuperAdmin: boolean;
  nowIso?: string;
}

export interface SetEnforcementModeResult {
  ok: boolean;
  mode: NetworkEnforcementMode;
  reason?: string;
}

/**
 * Audited mode flip. Caller must:
 *   1. Verify role === "super_admin"
 *   2. Collect typed CONFIRM + reason ≥10 chars (UI responsibility)
 *   3. Pass `confirmedSuperAdmin: true`
 *
 * The writer enforces reason length defensively. Audit entry is
 * append-only.
 */
export function setNetworkEnforcementMode(
  input: SetEnforcementModeInput,
): SetEnforcementModeResult {
  if (!input.confirmedSuperAdmin) {
    return { ok: false, mode: getNetworkEnforcementMode(), reason: "not_super_admin" };
  }
  if (!input.reason || input.reason.trim().length < 10) {
    return { ok: false, mode: getNetworkEnforcementMode(), reason: "reason_too_short" };
  }

  const s = safeStorage();
  const current = getNetworkEnforcementMode();
  if (s) s.setItem(STORAGE_KEY, input.toMode);

  const entry: EnforcementModeAuditEntry = {
    at: input.nowIso ?? new Date().toISOString(),
    fromMode: current,
    toMode: input.toMode,
    actorId: input.actorId,
    actorRole: input.actorRole,
    reason: input.reason.trim(),
  };

  if (s) {
    try {
      const prevRaw = s.getItem(AUDIT_STORAGE_KEY);
      const prev: EnforcementModeAuditEntry[] = prevRaw ? JSON.parse(prevRaw) : [];
      s.setItem(AUDIT_STORAGE_KEY, JSON.stringify([...prev, entry]));
    } catch {
      /* swallow — audit best-effort in alpha */
    }
  }

  return { ok: true, mode: input.toMode };
}

/** Read-only audit trail — append-only. */
export function getEnforcementModeAudit(): EnforcementModeAuditEntry[] {
  const s = safeStorage();
  if (!s) return [];
  try {
    const raw = s.getItem(AUDIT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as EnforcementModeAuditEntry[]) : [];
  } catch {
    return [];
  }
}

/**
 * UI helper — returns banner state for the 24h window after a flip.
 * Returns null when no banner should display.
 */
export function getEnforcementModeBanner(
  nowIso: string = new Date().toISOString(),
): { mode: NetworkEnforcementMode; flippedAt: string; expiresAt: string } | null {
  const audit = getEnforcementModeAudit();
  if (audit.length === 0) return null;
  const last = audit[audit.length - 1];
  const flippedAtMs = Date.parse(last.at);
  const expiresAtMs = flippedAtMs + BANNER_TTL_MS;
  if (Date.parse(nowIso) > expiresAtMs) return null;
  return {
    mode: last.toMode,
    flippedAt: last.at,
    expiresAt: new Date(expiresAtMs).toISOString(),
  };
}

/** Test-only reset. */
export function __resetEnforcementModeForTests(): void {
  const s = safeStorage();
  if (!s) return;
  s.removeItem(STORAGE_KEY);
  s.removeItem(AUDIT_STORAGE_KEY);
}
